/**
 * RecommendationCards — renders a VERTICAL list of tappable cards below an AI
 * message when the backend agent attached `agape_cards` via the
 * `recommend_content` tool. No horizontal slider: each card occupies the
 * full message-column width so it reads as a proper tile.
 *
 * Per-kind visual variants (driven by CSS `.agape-rec-card--{kind}`):
 *   - master   → round avatar (people)
 *   - practice → rounded-rect cover thumbnail (video)
 *   - feed     → rounded-rect cover thumbnail (live / post / event)
 *
 * Per-entity_type placeholder glyph (when `image_url` is missing) keeps the
 * card from collapsing into "blank square + title" when the backend hasn't
 * attached cover art for that entity yet.
 *
 * CORE-705 — this used to bail out on native (`Platform.OS !== 'web'`), which
 * combined badly with the backend's XOR rule: a turn that recommended content
 * had its quick-reply chips dropped server-side and then rendered nothing
 * tappable in the native thread. Native now gets an RN-primitive rendering of
 * the same cards; the web branch below is unchanged (its look lives in CSS).
 *
 * Contract: awesome-chat-api/docs/rag-implementation.md §4.
 */
import React, { useCallback } from 'react';
import { Image, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import type { RecommendationCard } from '../../types/rag';
import { navigateFromCard } from '../../utils/ragDeepLink';
import { useAuth } from '../../context/AuthContext';
import { emitProductEvent, ProductEvents } from '../../services/productEvents';
import { cardBadge, cardPlaceholder } from './recommendationCardChrome';

interface RecommendationCardsProps {
    cards: RecommendationCard[] | undefined;
    messageId: string | undefined;
    /**
     * Native only — the thread's foreground colours. The native chat sits on
     * dark artwork in `ChatThreadScreen` but on the app's light canvas inside
     * `MasterCircleChat`, so the host decides rather than the card.
     */
    nativeColors?: { text: string; muted: string; border: string; surface: string };
}

const NATIVE_FALLBACK_COLORS = {
    text: '#FFFFFF',
    muted: 'rgba(255,255,255,0.6)',
    border: 'rgba(255,255,255,0.14)',
    surface: 'rgba(255,255,255,0.08)',
};

const SOLAR = '#E98245';

export function RecommendationCards({ cards, messageId, nativeColors }: RecommendationCardsProps) {
    // Hook must run before any early return (rules-of-hooks); the chat always
    // renders inside AuthProvider, so this is safe.
    const { user } = useAuth();

    // Click-through on an Agapella recommendation — a client-only signal (the
    // backend counts impressions, not clicks). Shared by both renderings so the
    // native tap reports exactly what the web click does.
    const open = useCallback(
        (card: RecommendationCard) => {
            emitProductEvent(ProductEvents.recommendationClicked, {
                userId: user?.id,
                props: {
                    entity_type: card.entity_type,
                    entity_id: String(card.entity_id),
                    kind: card.kind,
                },
            });
            navigateFromCard(card);
        },
        [user?.id],
    );

    if (!cards || cards.length === 0) return null;

    // De-dupe by entity identity: the "compare masters" flow (and any merge of
    // recommend_content + find_masters results) can attach the same master
    // twice, which then rendered as duplicate tiles. First occurrence wins.
    const uniqueCards = cards.filter((card, idx) => {
        const sig = `${card.entity_type}:${card.entity_id}`;
        return cards.findIndex((c) => `${c.entity_type}:${c.entity_id}` === sig) === idx;
    });

    if (Platform.OS !== 'web') {
        const c = nativeColors || NATIVE_FALLBACK_COLORS;
        return (
            <View style={nativeStyles.list} testID="agape-rec-cards">
                {uniqueCards.map((card) => {
                    const badge = cardBadge(card);
                    const placeholder = cardPlaceholder(card);
                    // Key on entity identity (not array index), so React never treats
                    // two arrays containing the same entity as new nodes.
                    const key = `${messageId || 'm'}-rc-${card.entity_type}-${card.entity_id}`;
                    // `master` reads as a person → round avatar, like the web variant.
                    const thumbShape = card.kind === 'master' ? nativeStyles.thumbRound : nativeStyles.thumbRect;
                    return (
                        <Pressable
                            key={key}
                            testID="agape-rec-card"
                            accessibilityRole="button"
                            accessibilityLabel={`${badge}: ${card.title}`}
                            onPress={() => open(card)}
                            style={({ pressed }) => [
                                nativeStyles.card,
                                { backgroundColor: c.surface, borderColor: c.border },
                                pressed && nativeStyles.cardPressed,
                            ]}
                        >
                            {card.image_url ? (
                                <Image source={{ uri: card.image_url }} style={[nativeStyles.thumb, thumbShape]} />
                            ) : (
                                <View
                                    style={[
                                        nativeStyles.thumb,
                                        thumbShape,
                                        nativeStyles.thumbPlaceholder,
                                        { borderColor: c.border },
                                    ]}
                                >
                                    <Text style={nativeStyles.placeholderGlyph}>{placeholder}</Text>
                                </View>
                            )}
                            <View style={nativeStyles.body}>
                                <Text style={nativeStyles.badge}>{badge.toUpperCase()}</Text>
                                <Text style={[nativeStyles.title, { color: c.text }]} numberOfLines={2}>
                                    {card.title}
                                </Text>
                                {!!card.subtitle && (
                                    <Text style={[nativeStyles.subtitle, { color: c.muted }]} numberOfLines={1}>
                                        {card.subtitle}
                                    </Text>
                                )}
                                {!!card.reason && (
                                    <Text style={[nativeStyles.reason, { color: c.muted }]} numberOfLines={2}>
                                        {card.reason}
                                    </Text>
                                )}
                            </View>
                        </Pressable>
                    );
                })}
            </View>
        );
    }

    return (
        <div className="agape-rec-cards" data-testid="agape-rec-cards">
            {uniqueCards.map((card) => {
                const badge = cardBadge(card);
                const placeholder = cardPlaceholder(card);
                const key = `${messageId || 'm'}-rc-${card.entity_type}-${card.entity_id}`;
                const ariaLabel = `${badge}: ${card.title}`;
                return (
                    <button
                        key={key}
                        type="button"
                        className={`agape-rec-card agape-rec-card--${card.kind}`}
                        data-testid="agape-rec-card"
                        data-entity-type={card.entity_type}
                        aria-label={ariaLabel}
                        onClick={(e) => {
                            e.stopPropagation();
                            open(card);
                        }}
                    >
                        {card.image_url ? (
                            <span
                                className="agape-rec-card__img"
                                style={{ backgroundImage: `url(${card.image_url})` }}
                                aria-hidden="true"
                            />
                        ) : (
                            <span
                                className="agape-rec-card__img agape-rec-card__img--placeholder"
                                aria-hidden="true"
                            >
                                {placeholder}
                            </span>
                        )}
                        <span className="agape-rec-card__body">
                            <span className="agape-rec-card__badge">{badge}</span>
                            <span className="agape-rec-card__title">{card.title}</span>
                            {card.subtitle ? (
                                <span className="agape-rec-card__subtitle">{card.subtitle}</span>
                            ) : null}
                            {card.reason ? (
                                <span className="agape-rec-card__reason">{card.reason}</span>
                            ) : null}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/**
 * CORE-705 — native mirror of `.agape-rec-card` in the web stylesheet: a full
 * width row, thumbnail left, badge/title/subtitle/reason stacked right.
 */
const nativeStyles = StyleSheet.create({
    list: { gap: 8 },
    card: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 10,
        borderRadius: 14,
        borderWidth: 1,
    },
    cardPressed: { opacity: 0.7 },
    thumb: { width: 52, height: 52 },
    thumbRound: { borderRadius: 26 },
    thumbRect: { borderRadius: 10 },
    thumbPlaceholder: {
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        backgroundColor: 'rgba(252,116,11,0.12)',
    },
    placeholderGlyph: { fontSize: 22, color: SOLAR },
    body: { flex: 1, gap: 2 },
    badge: { fontSize: 10, fontWeight: '700', letterSpacing: 0.6, color: SOLAR },
    title: { fontSize: 14, fontWeight: '600' },
    subtitle: { fontSize: 12 },
    reason: { fontSize: 12, fontStyle: 'italic' },
});

export default RecommendationCards;
